import { useTranslation } from 'react-i18next';

import { BACKEND_ERROR_CODE_TRANSLATIONS, BackendErrorCode } from '@/modules/common/api/errors';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { INVOICE_SYNC_ERROR_I18N_KEYS } from '../../constants/invoice-sync-error-code-i18n';
import { INVOICE_SYNC_ERROR_REASON_I18N_KEYS } from '../../constants/invoice-sync-error-reason-i18n';
import { InvoiceListItem, InvoiceSyncError, InvoiceSyncOperation } from '../../types';

interface InvoiceErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: InvoiceListItem | null;
  syncOperation: InvoiceSyncOperation | null;
  syncError: InvoiceSyncError | null;
  onRetry?: (invoice: InvoiceListItem) => void;
  isRetrying?: boolean;
}

export const InvoiceErrorModal = ({
  isOpen,
  onClose,
  invoice,
  syncOperation,
  syncError,
  onRetry,
  isRetrying = false,
}: InvoiceErrorModalProps) => {
  const { t } = useTranslation(['authenticated.invoices', 'common'], {
    keyPrefix: 'modals.invoiceError',
  });
  const { t: tCommon } = useTranslation('common');
  const { t: tGlobal } = useTranslation();

  const handleRetry = () => {
    if (invoice && onRetry) {
      onRetry(invoice);
    }
  };

  const titleKey =
    syncOperation === InvoiceSyncOperation.Create
      ? 'title.create'
      : syncOperation === InvoiceSyncOperation.Update
        ? 'title.update'
        : 'title.default';
  const isIntegrationValidationError =
    syncError?.code === BackendErrorCode.ENTITY_SYNC_INTEGRATION_VALIDATION_ERROR;
  const reasonKey =
    isIntegrationValidationError && syncError.reason
      ? INVOICE_SYNC_ERROR_REASON_I18N_KEYS[syncError.reason]
      : null;
  const reasonText = reasonKey ? t(reasonKey) : null;
  const syncErrorKey = syncError ? INVOICE_SYNC_ERROR_I18N_KEYS[syncError.code] : null;
  const syncErrorText = syncErrorKey ? t(syncErrorKey) : null;
  const codeTranslationKey = syncError ? BACKEND_ERROR_CODE_TRANSLATIONS[syncError.code] : null;
  const codeText = codeTranslationKey ? tGlobal(codeTranslationKey, { defaultValue: '' }) : null;
  const errorText =
    reasonText || syncErrorText || codeText || syncError?.message?.trim() || t('fallback');
  const details = isIntegrationValidationError ? (syncError?.details ?? []) : [];
  const canRetry = Boolean(invoice && onRetry);

  return (
    <ConfirmationModal
      onClose={onClose}
      open={isOpen}
      confirmText={canRetry ? t('retry') : tCommon('close')}
      onConfirm={canRetry ? handleRetry : onClose}
      title={t(titleKey)}
      cancelText={t('cancel')}
      confirmVariant="default"
      hideCloseIcon
      hideCancelButton={!canRetry}
      isLoading={isRetrying}
    >
      <div className="text-sm text-muted-foreground">
        <p>{errorText}</p>
        {details.length > 0 && (
          <ul className="mt-2 list-inside list-disc space-y-1">
            {details.map((detail, index) => (
              <li key={`${detail}-${index}`}>{detail}</li>
            ))}
          </ul>
        )}
      </div>
    </ConfirmationModal>
  );
};
